import React, { useState } from 'react';
import MultiSelectBox from '@Common/MultiSelectBox/MultiSelectBox';
import MultiValueInput from '@Common/MultiValueInput';

interface WorkflowValidation {
	message: string;
	type: string;
	value: string;
	workflowEmails: Array<string>;
}

interface WorkflowAction {
	actionId: string;
	name: string;
	validation: WorkflowValidation;
}

interface WorkflowOption {
	eventId: string;
	name: string;
	actions: Array<WorkflowAction>;
}

export interface SelectedWorkflow {
	eventId: string;
	actionId: string;
	workflowEmails: Array<string>;
}

interface WorkflowsComponentProps {
	workflows: Array<WorkflowOption>;
	selectedWorkflows: Array<SelectedWorkflow>;
	onChange: (workflows: Array<SelectedWorkflow>) => void;
	isDisabled?: boolean;
}

const emptyDraft: SelectedWorkflow = { eventId: '', actionId: '', workflowEmails: [] };

export const WorkflowsComponentForm = ({ workflows, selectedWorkflows, onChange, isDisabled = false }: WorkflowsComponentProps) => {
	// null = box closed, 'new' = adding, number = editing existing index
	const [editingIndex, setEditingIndex] = useState<number | 'new' | null>(null);
	const [draft, setDraft] = useState<SelectedWorkflow>(emptyDraft);

	const list = selectedWorkflows ?? [];
	const boxOpen = editingIndex !== null;

	const draftActions = workflows.find((w) => w.eventId === draft.eventId)?.actions ?? [];
	const draftValidation = draftActions.find((a) => a.actionId === draft.actionId)?.validation;
	const draftShowEmails = draftValidation?.type === 'email';

	// Fall back to the raw id when options are missing (e.g. the options fetch
	// failed) so saved workflows still render something instead of going blank.
	const eventName = (eventId: string) => workflows.find((w) => w.eventId === eventId)?.name ?? eventId ?? '';
	const actionName = (eventId: string, actionId: string) =>
		workflows.find((w) => w.eventId === eventId)?.actions.find((a) => a.actionId === actionId)?.name ?? actionId ?? '';

	const openAdd = () => {
		setDraft(emptyDraft);
		setEditingIndex('new');
	};

	const openEdit = (index: number) => {
		setDraft({ ...list[index], workflowEmails: list[index].workflowEmails ?? [] });
		setEditingIndex(index);
	};

	const cancel = () => {
		setDraft(emptyDraft);
		setEditingIndex(null);
	};

	const onTriggerChange = (eventId: string) => {
		// reset downstream selections when the trigger changes
		setDraft({ eventId, actionId: '', workflowEmails: [] });
	};

	const onActionChange = (actionId: string) => {
		setDraft((prev) => ({ ...prev, actionId, workflowEmails: [] }));
	};

	const onEmailsChange = (workflowEmails: Array<string>) => {
		setDraft((prev) => ({ ...prev, workflowEmails }));
	};

	const save = () => {
		// require at least a trigger + action before committing
		if (!draft.eventId || !draft.actionId) {
			return;
		}
		const updated = [...list];
		if (editingIndex === 'new') {
			updated.push(draft);
		} else if (typeof editingIndex === 'number') {
			updated[editingIndex] = draft;
		}
		onChange(updated);
		cancel();
	};

	const remove = (index: number) => {
		onChange(list.filter((_, i) => i !== index));
		if (editingIndex === index) {
			// removed the row being edited
			cancel();
		} else if (typeof editingIndex === 'number' && index < editingIndex) {
			// removed a row above the one being edited — shift the index down
			// so Save still targets the same workflow
			setEditingIndex(editingIndex - 1);
		}
	};

	const canSave = Boolean(draft.eventId && draft.actionId);

	return (
		<div className='col-12 workflows-component'>
			<div className='workflows-header d-flex justify-content-between align-items-center'>
				<span className='workflows-title'>Workflow Automations</span>
				{!isDisabled && !boxOpen && (
					<button type='button' className='btn btn-link' onClick={openAdd}>
						<i className='fa-regular fa-circle-plus'></i>
						<span className='mx-1'>Add Workflow</span>
					</button>
				)}
			</div>

			{list.length === 0 && !boxOpen && (
				<div className='workflows-empty'>{'No workflows configured. Click "Add Workflow" to create automation rules.'}</div>
			)}

			{/* Existing workflows */}
			{list.map((workflow, index) => {
				if (editingIndex === index) {
					return null; // rendered inline as the edit box below
				}
				return (
					<div key={`workflow-${index}`} className='workflow-row d-flex justify-content-between'>
						<div className='workflow-row-body'>
							<div className='workflow-line'>
								<span className='workflow-badge workflow-badge-trigger'>Trigger</span>
								<span className='workflow-line-text'>{eventName(workflow.eventId)}</span>
							</div>
							<div className='workflow-arrow'>↓</div>
							<div className='workflow-line'>
								<span className='workflow-badge workflow-badge-action'>Action</span>
								<span className='workflow-line-text'>{actionName(workflow.eventId, workflow.actionId)}</span>
							</div>
							{workflow.workflowEmails && workflow.workflowEmails.length > 0 && (
								<div className='workflow-row-emails'>{workflow.workflowEmails.join(', ')}</div>
							)}
						</div>
						{!isDisabled && (
							<div className='workflow-row-actions d-flex'>
								<div className='icon-black custom-size-sup' onClick={() => openEdit(index)}>
									<i className='fa-light fa-pen-to-square'></i>
								</div>
								<div className='icon custom-size-sup' onClick={() => remove(index)}>
									<span className='fa-light fa-trash-can'></span>
								</div>
							</div>
						)}
					</div>
				);
			})}

			{/* Add / edit box */}
			{boxOpen && (
				<div className='workflow-box'>
					<div className='workflow-step'>
						<div className='workflow-step-num'>1</div>
						<div className='workflow-step-body'>
							<label className='title'>Select Trigger</label>
							<MultiSelectBox
								name='workflow-trigger'
								options={workflows}
								idField='eventId'
								valueField='name'
								selectedValues={draft.eventId !== '' ? String(draft.eventId) : ''}
								onChange={(eventId) => onTriggerChange(eventId)}
								isMulti={false}
								isRequired={false}
								isDisabled={isDisabled}
								placeholder='Choose a trigger event...'
							/>
						</div>
					</div>

					{draft.eventId && (
						<div className='workflow-step'>
							<div className='workflow-step-num'>2</div>
							<div className='workflow-step-body'>
								<label className='title'>Select Action</label>
								<MultiSelectBox
									name='workflow-action'
									options={draftActions}
									idField='actionId'
									valueField='name'
									selectedValues={draft.actionId !== '' ? String(draft.actionId) : ''}
									onChange={(actionId) => onActionChange(actionId)}
									isMulti={false}
									isRequired={false}
									isDisabled={isDisabled}
									placeholder='Choose an action...'
								/>
							</div>
						</div>
					)}

					{draft.actionId && draftShowEmails && (
						<div className='workflow-step'>
							<div className='workflow-step-num'>3</div>
							<div className='workflow-step-body'>
								<label className='title'>Notify</label>
								<MultiValueInput
									name='workflow-email'
									emails={draft.workflowEmails ?? []}
									setEmails={(emails) => onEmailsChange(emails)}
									isDisabled={isDisabled}
									label=''
									placeholder='Type an email and press Enter'
								/>
							</div>
						</div>
					)}

					<div className='workflow-box-footer d-flex justify-content-end'>
						<button type='button' className='btn btn-link-custom' onClick={cancel}>
							Cancel
						</button>
						<button type='button' className='btn btn-primary' onClick={save} disabled={!canSave}>
							Save Workflow
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default WorkflowsComponentForm;
